/**
 * Service for CBE (Crossroads Bank for Enterprises) API
 * API Documentation: https://cbeapi.be/docs/api#/
 */

const CBE_API_BASE = 'https://cbeapi.be/api/v1';
const CBE_API_KEY = import.meta.env.VITE_CBEAPI_API_KEY;

/**
 * Search enterprise by VAT number (numéro d'entreprise)
 * @param {string} vatNumber - VAT number (e.g., "0123.456.789" or "BE0123456789")
 * @returns {Promise<Object>} Enterprise data
 */
export const searchByVatNumber = async (vatNumber) => {
    try {
        // Clean the VAT number (remove spaces, dots, BE prefix)
        const cleanVat = vatNumber
            .replace(/\s/g, '')
            .replace(/\./g, '')
            .replace(/^BE/i, '');

        // Format as 10 digits with leading zeros if needed
        const formattedVat = cleanVat.padStart(10, '0');

        console.log('Searching CBE for VAT:', formattedVat);

        const response = await fetch(`${CBE_API_BASE}/company/${formattedVat}`, {
            method: 'GET',
            headers: {
                'Accept': 'application/json',
                'Authorization': `Bearer ${CBE_API_KEY}`,
            }
        });

        if (!response.ok) {
            if (response.status === 404) {
                return { success: false, message: 'Entreprise non trouvée' };
            }
            if (response.status === 401) {
                return { success: false, message: 'Erreur d\'authentification API' };
            }
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        console.log('CBE response:', data);

        // Transform CBE response to our format
        return {
            success: true,
            data: transformCbeData(data.data)
        };
    } catch (error) {
        console.error('Error searching CBE:', error);
        return {
            success: false,
            message: error.message || 'Erreur lors de la recherche'
        };
    }
};

/**
 * Search enterprises by name
 * @param {string} name - Enterprise name
 * @returns {Promise<Object>} Search results
 */
export const searchByName = async (name) => {
    try {
        console.log('Searching CBE by name:', name);

        const response = await fetch(`${CBE_API_BASE}/company/search?name=${encodeURIComponent(name)}`, {
            method: 'GET',
            headers: {
                'Accept': 'application/json',
                'Authorization': `Bearer ${CBE_API_KEY}`,
            }
        });

        if (!response.ok) {
            if (response.status === 404) {
                return { success: true, data: [] };
            }
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        console.log('CBE search response:', data);

        // Transform results - API returns array of companies
        const results = (data.data || data || []).map(item => transformCbeData(item));

        return {
            success: true,
            data: results
        };
    } catch (error) {
        console.error('Error searching CBE by name:', error);
        return {
            success: false,
            message: error.message || 'Erreur lors de la recherche'
        };
    }
};

/**
 * Transform CBE API response to our place format
 * @param {Object} cbeData - Raw CBE data
 * @returns {Object} Transformed place data
 */
const transformCbeData = (cbeData) => {
    if (!cbeData) return null;

    // Extract address from CBE data
    const address = cbeData.address || {};

    // Extract denomination (company name)
    const denomination = cbeData.denomination ||
                        cbeData.denomination_with_legal_form ||
                        cbeData.commercial_name ||
                        '';

    // Extract legal form
    const legalForm = cbeData.juridical_form ||
                     cbeData.pretty_type ||
                     '';

    // Use formatted address from BCE API directly
    const googleAddress = address.formatted_address ||
                         address.full_address ||
                         cbeData.address_formatted ||
                         '';

    return {
        placeName: denomination,
        tvaNum: formatVatNumber(cbeData.cbe_number || ''),
        googleAddress: googleAddress,
        street: address.street || '',
        number: address.street_number || '',
        box: address.box || '',
        zip: address.post_code || '',
        city: address.city || '',
        firmType: legalForm,
        phone: cbeData.contact_infos?.phone || '',
        email: cbeData.contact_infos?.email || '',
        website: cbeData.contact_infos?.web || '',
        status: cbeData.status === 'active' ? 1 : 0,
        // Additional CBE data
        cbeNumber: cbeData.cbe_number || '',
        cbeNumberFormatted: cbeData.cbe_number_formatted || '',
        startDate: cbeData.start_date || '',
        activities: cbeData.nace_activities || []
    };
};

/**
 * Format VAT number to Belgian format
 * @param {string} number - Raw number
 * @returns {string} Formatted VAT number (BE 0123.456.789)
 */
const formatVatNumber = (number) => {
    if (!number) return '';
    const clean = number.toString().replace(/\D/g, '').padStart(10, '0');
    return `BE ${clean.slice(0, 4)}.${clean.slice(4, 7)}.${clean.slice(7, 10)}`;
};

export default {
    searchByVatNumber,
    searchByName
};
